// src/pages/InventoryEditItem.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";

export default function InventoryEditItem() {
    const { itemId } = useParams();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        barcode: "",
        price: "",
        cost: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        async function load() {
            setError("");
            setLoading(true);
            try {
                const ref = doc(db, "itemsEK", itemId);
                const snap = await getDoc(ref);
                if (!snap.exists()) {
                    setError("Article introuvable.");
                    setLoading(false);
                    return;
                }
                const data = snap.data() || {};
                setForm({
                    name: data.name || data.title || "",
                    barcode: data.barcode || "",
                    price: data.price ?? "",
                    cost: data.cost ?? "",
                });
            } catch (e) {
                console.error(e);
                setError("Erreur lors du chargement de l'article.");
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [itemId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((s) => ({ ...s, [name]: value }));
    };

    const validate = () => {
        if (!form.name.trim()) return "Le nom est obligatoire.";
        if (form.price !== "" && isNaN(Number(form.price)))
            return "Prix invalide.";
        if (form.cost !== "" && isNaN(Number(form.cost)))
            return "Coût invalide.";
        return "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        const v = validate();
        if (v) {
            setError(v);
            return;
        }

        setSaving(true);
        try {
            const trimmedName = form.name.trim();
            const ref = doc(db, "itemsEK", itemId);
            await updateDoc(ref, {
                name: trimmedName,
                nameLower: trimmedName.toLowerCase(),
                barcode: form.barcode || "",
                price: form.price !== "" ? Number(form.price) : null,
                cost: form.cost !== "" ? Number(form.cost) : null,
                updatedAt: serverTimestamp(),
            });

            setSuccess("Article mis à jour avec succès.");
            setTimeout(() => {
                navigate("/inventory");
            }, 400);
        } catch (e) {
            console.error(e);
            setError("Impossible de sauvegarder les modifications.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="mb-1">Éditer l’article</h3>
                    <p className="text-muted mb-0">
                        ID : <code>{itemId}</code>
                    </p>
                </div>
                <div>
                    <Link to="/admin/inventory" className="btn btn-outline-secondary">
                        ← Retour à l’inventaire
                    </Link>

                </div>
            </div>

            <div className="card shadow-sm">
                <div className="card-body">
                    {loading ? (
                        <div className="d-flex align-items-center justify-content-center py-4">
                            <span className="spinner-border me-2" />
                            <span>Chargement…</span>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {error && (
                                <div className="alert alert-danger py-2">{error}</div>
                            )}
                            {success && (
                                <div className="alert alert-success py-2">{success}</div>
                            )}

                            <div className="mb-3 form-floating">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="edit-name"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Nom"
                                    required
                                />
                                <label htmlFor="edit-name">Nom de l’article *</label>
                            </div>

                            <div className="mb-3 form-floating">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="edit-barcode"
                                    name="barcode"
                                    value={form.barcode}
                                    onChange={handleChange}
                                    placeholder="Code-barres"
                                />
                                <label htmlFor="edit-barcode">Code-barres</label>
                            </div>

                            <div className="row g-3">
                                <div className="col-md-6 form-floating">
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="edit-price"
                                        name="price"
                                        value={form.price}
                                        onChange={handleChange}
                                        placeholder="Prix (FCFA)"
                                        min="0"
                                    />
                                    <label htmlFor="edit-price">Prix de vente (FCFA)</label>
                                </div>
                                <div className="col-md-6 form-floating">
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="edit-cost"
                                        name="cost"
                                        value={form.cost}
                                        onChange={handleChange}
                                        placeholder="Coût (FCFA)"
                                        min="0"
                                    />
                                    <label htmlFor="edit-cost">Coût d’achat (FCFA)</label>
                                </div>
                            </div>

                            <div className="mt-4 d-flex justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate(-1)}
                                    disabled={saving}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Enregistrement…
                                        </>
                                    ) : (
                                        "Enregistrer"
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
